import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import EmployeeInduction from '../components/EmployeeInduction';
import LicenceUpload from '../components/LicenceUpload';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' }) : '');
const fmtAbn  = (a) => (a ? a.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4') : '');
const fmtBsb  = (b) => (b ? b.replace(/(\d{3})(\d{3})/, '$1-$2') : '');

const Grid = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'0 12px' }}>{children}</div>
);

const show = (label, value, mono) => (
  <Field label={label}>
    <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color: value ? 'var(--text)' : 'var(--hint)', minHeight:'38px', ...(mono ? { fontFamily:'JetBrains Mono,monospace', fontSize:'13px' } : {}) }}>
      {value || '—'}
    </div>
  </Field>
);

// CS Staff → a staff member's full details
export default function EmployeeDetail() {
  const { id }   = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner  = user?.role === 'owner';

  const [e,       setE]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice,  setNotice]  = useState(null);

  const load = useCallback(() => {
    api.get(`/employees/${id}`)
      .then(({ data }) => setE(data.employee))
      .catch(err => setNotice({ type:'error', text: err.response?.data?.message || 'Failed to load this staff member' }))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!await confirmDelete(`"${String(e.fullName).replace(/[&<>"']/g, ch => '&#' + ch.charCodeAt(0) + ';')}" and all of their details (including bank and medical information) and their induction record will be permanently removed.`)) return;
    try { await api.delete(`/employees/${id}`); navigate('/staff'); }
    catch (err) { setNotice({ type:'error', text: err.response?.data?.message || 'Failed to delete' }); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );
  if (!e) return <div style={{ padding:'40px', color:'var(--red)', textAlign:'center' }}>{notice?.text || 'Staff member not found'}</div>;

  return (
    <div style={T.page}>
      <PageHeader title={e.staffId ? `${e.fullName} · ${e.staffId}` : e.fullName} onBack={()=>navigate('/staff')} actions={
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={()=>navigate(`/staff/${id}/edit`)} title="Edit" style={{ ...T.btnIcon, color:'var(--amber)', borderColor:'rgba(133,79,11,0.25)' }}>
            <i className="ti ti-edit" aria-label="Edit"/>
          </button>
          {isOwner && (
            <button onClick={handleDelete} title="Delete" style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
              <i className="ti ti-trash" aria-label="Delete"/>
            </button>
          )}
        </div>
      }/>

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 60px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        <Section title="Induction" accent>
          <EmployeeInduction employee={e} onChanged={load} onNotice={setNotice}/>
        </Section>

        <Section title="Staff Record" accent>
          <Grid>
            {show('Staff ID', e.staffId, true)}
            {show('Start date', fmtDate(e.startDate))}
            {show('Email', e.email)}
          </Grid>
          {e.registrationId && (
            <button onClick={()=>navigate(`/staff-registrations/${e.registrationId}`)} style={{ ...T.btnSecondary, padding:'6px 12px', fontSize:'12px' }}>
              <i className="ti ti-id" aria-hidden="true"/> View self-registration & driver licence
            </button>
          )}
        </Section>

        <Section title="Employee Details" accent>
          <Grid>
            {show('First name(s)', e.firstNames)}
            {show('Surname', e.surname)}
          </Grid>
          {show('Home address', e.homeAddress)}
          <Grid>
            {show('Date of birth', fmtDate(e.dateOfBirth))}
            {show('Nationality', e.nationality)}
            {show('Mobile tel number', e.mobilePhone, true)}
          </Grid>
        </Section>

        <Section title="Emergency Contact" accent>
          <Grid>
            {show('Name', e.emergencyName)}
            {show('Contact tel number', e.emergencyPhone, true)}
          </Grid>
        </Section>

        <Section title="Bank Account Details for Payroll" accent>
          <Grid>
            {show('Account name', e.accountName)}
            {show('BSB number', fmtBsb(e.bsb), true)}
            {show('Account number', e.accountNumber, true)}
            {show('Name of bank', e.bankName)}
          </Grid>
        </Section>

        <Section title="ABN" accent>
          {show('ABN', fmtAbn(e.abn), true)}
        </Section>

        <Section title="Driver Licence" accent>
          <Grid>
            {show('Current driver licence', e.hasDriverLicence ? 'Yes' : 'No')}
            {e.hasDriverLicence && show('Licence number', e.licenceNumber, true)}
            {e.hasDriverLicence && show('Expiry date', fmtDate(e.licenceExpiry))}
          </Grid>
          {e.hasDriverLicence && ['front', 'back'].map(side => (
            <LicenceUpload key={side} label={side === 'front' ? 'Front' : 'Back'} meta={e[side === 'front' ? 'licenceFront' : 'licenceBack']}
              fetchBlob={() => api.get(`/employees/${id}/licence/${side}`, { responseType:'blob' }).then(r => r.data)}/>
          ))}
        </Section>

        <Section title="Medical Information" accent>
          <Grid>
            {show('Doctor’s name', e.doctorName)}
            {show('Doctor’s contact tel number', e.doctorPhone, true)}
          </Grid>
          {show('Doctor’s address', e.doctorAddress)}
          {show('Allergies', e.allergies)}
          {show('Medications', e.medications)}
        </Section>

        <Section title="Languages" accent>
          {show('Languages other than English', e.speaksOtherLanguage ? 'Yes' : 'No')}
          {e.speaksOtherLanguage && (
            <>
              {show('Languages spoken', e.languages)}
              <Grid>
                {show('Knowledge', cap(e.languageLevel))}
                {show('Spoken / Written', cap(e.languageMode))}
              </Grid>
            </>
          )}
        </Section>

        <Section title="Signed and Dated" accent>
          {e.signature
            ? <img src={e.signature} alt="Employee signature" style={{ width:'100%', maxWidth:'360px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
            : <div style={{ fontSize:'13px', color:'var(--hint)' }}>Not signed</div>}
          {e.signedDate && <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'6px' }}>Dated {fmtDate(e.signedDate)}</div>}
        </Section>
      </div>
    </div>
  );
}
